import { useState } from 'react'

import { useRetrainApi } from '@/common/api/modules/admin'
import {
  useAnalyticsAlertsGetApi,
  useAnalyticsAlgorithmPerformanceGetApi,
  useAnalyticsOverviewGetApi,
  useAnalyticsServiceSummaryGetApi
} from '@/common/api/modules/analytics'
import { RetrainResponse } from '@/common/api/params/admin'
import { algorithmName } from '@/common/app/modules/algorithm'
import { behaviorActionTypeName } from '@/common/app/modules/recommend/behaviorLog'
import { BehaviorActionType } from '@/common/model/recommend/behavior'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import BarChart from '@/components/view/atoms/chart/BarChart'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import PieChart from '@/components/view/atoms/chart/PieChart'
import Heading from '@/components/view/atoms/heading/Heading'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AlertSection from '@/components/container/page/admin/dashboard/AlertSection'
import ServiceSummaryCard from '@/components/container/page/admin/dashboard/ServiceSummaryCard'
import ServiceTrendChart from '@/components/container/page/admin/dashboard/ServiceTrendChart'

const TrainingBlock = () => {
  const [trainResult, setTrainResult] = useState<RetrainResponse | null>(null)
  const { trigger, isMutating } = useRetrainApi({
    onSuccess: (data) => setTrainResult(data),
    onError: () => setTrainResult({ status: 'error', message: '再学習に失敗しました' })
  })

  return (
    <Area>
      <Heading>モデル学習</Heading>
      <AreaContent>
        <Text>最新のインタラクションデータで全レコメンドエンジンを再学習します。</Text>
        <Button color={ButtonColor.PRIMARY_BRAND} onClick={() => trigger({})} loading={isMutating}>
          {isMutating ? '学習中...' : '全モデルを再学習'}
        </Button>
        {trainResult && (
          <pre className='text-sm text-gray-500'>{JSON.stringify(trainResult, null, 2)}</pre>
        )}
      </AreaContent>
    </Area>
  )
}

const AdminDashboardPage = () => {
  let { data: analyticsOverview, error: analyticsOverviewError } = useAnalyticsOverviewGetApi()
  const { data: algorithmPerformance, error: algorithmPerformanceError } =
    useAnalyticsAlgorithmPerformanceGetApi()
  const { data: serviceSummary, error: serviceSummaryError } = useAnalyticsServiceSummaryGetApi()
  const { data: alerts } = useAnalyticsAlertsGetApi()

  return (
    <DetailPageLoader
      error={analyticsOverviewError || algorithmPerformanceError || serviceSummaryError}
      data={analyticsOverview && algorithmPerformance && serviceSummary}
    >
      {analyticsOverview && algorithmPerformance && serviceSummary && (
        <Main type={MainLayoutType.FULL}>
          <PageTitle title={'ダッシュボード'} />
          <Content>
            {/* 要対応アラート */}
            {alerts && <AlertSection data={alerts} />}

            {/* 全体サマリー */}
            <Area noBorder>
              <SummaryItemList>
                <SummaryItem>
                  <Text weight='bold'>登録サイト数</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {analyticsOverview.total_sites.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>有償化サイト</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {analyticsOverview.paid_sites.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                  <Text size={12} color={TextColor.SECONDARY}>
                    いずれかの有償機能を有効化したサイト
                  </Text>
                </SummaryItem>
                <SummaryItem>
                  <Text weight='bold'>30日アクティブ</Text>
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {analyticsOverview.active_sites_30d.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                  <Text size={12} color={TextColor.SECONDARY}>
                    直近30日でデータ更新があったサイト
                  </Text>
                </SummaryItem>
                <SummaryItem>
                  <IconText
                    text='ユーザー数'
                    iconType={IconType.USER_SQUARE}
                    color={IconColor.PURPLE}
                    textColor={TextColor.PURPLE}
                    direction='horizon'
                  />
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {analyticsOverview.total_users.toLocaleString()}
                    </Text>
                    <Text size={14}>人</Text>
                  </ItemAlign>
                </SummaryItem>
                <SummaryItem>
                  <IconText
                    text='商品数'
                    iconType={IconType.DASHBOARD}
                    color={IconColor.ORANGE}
                    textColor={TextColor.ORANGE}
                    direction='horizon'
                  />
                  <ItemAlign>
                    <Text weight='bold' size={24}>
                      {analyticsOverview.total_products.toLocaleString()}
                    </Text>
                    <Text size={14}>件</Text>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
            </Area>

            {/* サービス別サマリーカード */}
            <Heading>サービス別 利用状況・事業効果</Heading>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {serviceSummary.services.map((s) => (
                <ServiceSummaryCard key={s.key} service={s} />
              ))}
            </div>

            {/* 直近30日トレンド */}
            <Area>
              <ChartWrapper>
                <ChartTitle>直近30日 サービス別業務量トレンド</ChartTitle>
                <ServiceTrendChart services={serviceSummary.services} />
              </ChartWrapper>
            </Area>

            {/* レコメンド系の従来チャート */}
            <div className='flex gap-6'>
              <div className='w-[calc(50%-0.5rem)]'>
                <Area>
                  <ChartWrapper>
                    <ChartTitle>イベント種別</ChartTitle>
                    <BarChart
                      labels={Object.keys(analyticsOverview?.events_by_type).map((event) =>
                        event == 'view'
                          ? 'サイト閲覧'
                          : behaviorActionTypeName(event as BehaviorActionType)
                      )}
                      items={Object.keys(analyticsOverview?.events_by_type).map((event) => ({
                        title:
                          event == 'view'
                            ? 'サイト閲覧'
                            : behaviorActionTypeName(event as BehaviorActionType),
                        values: [
                          analyticsOverview?.events_by_type[event as BehaviorActionType] || 0
                        ]
                      }))}
                      single
                    />
                  </ChartWrapper>
                </Area>
              </div>
              <div className='w-[calc(50%-0.5rem)]'>
                <Area>
                  <ChartWrapper>
                    <ChartTitle>アルゴリズム性能</ChartTitle>
                    {Object.keys(algorithmPerformance).length > 0 ? (
                      <PieChart
                        centerLabel='総レコメンド数'
                        items={Object.keys(algorithmPerformance).map((key) => ({
                          label:
                            algorithmName(key as keyof typeof algorithmPerformance) +
                            ` (CTR: ${algorithmPerformance[key as keyof typeof algorithmPerformance].ctr}%)`,
                          value:
                            algorithmPerformance[key as keyof typeof algorithmPerformance]
                              .total_recommendations
                        }))}
                      />
                    ) : (
                      <div className='py-10 text-center'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          データがありません
                        </Text>
                      </div>
                    )}
                  </ChartWrapper>
                </Area>
              </div>
            </div>
            <TrainingBlock />
          </Content>
        </Main>
      )}
    </DetailPageLoader>
  )
}

export default AdminDashboardPage
